import { useCallback, useEffect, useState } from "react";
import { orderBy } from "lodash";
import { AmplifyGet } from "@sunrun/sales-experience-shared/amplify-api-wrapper";
import { SplatCheckoutDocument } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { getPdfUrl, getWordUrl } from "utils/fileUtils";
import { logger } from "providers/logger";
import { useAuthToken } from "hooks/useAuthToken";

type UseCheckDocViewing = {
  viewDocLoading: boolean;
  viewDocument: () => void;
  viewDocError?: Error;
  resetError: () => void;
};

const findMostRecentAttachment = (
  attachments: SplatCheckoutDocument[]
): SplatCheckoutDocument => {
  return orderBy(attachments, ["lastModifiedDate"], ["desc"])[0];
};

const useCheckoutDocViewing = (
  prospectId: string,
  proposalId: string,
  documentType?: string
): UseCheckDocViewing => {
  const hybridToken = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const [isLoading, setIsLoading] = useState(false);
  const [viewDocError, setViewDocError] = useState<Error>();
  const [recentDocumentUrl, setRecentDocumentUrl] = useState<string>();

  const createUrl = useCallback(
    async (doc: string) => {
      if (!doc) {
        setViewDocError(
          new Error(`No ${documentType} document available to view`)
        );
        return;
      }
      let url = "";
      if (doc.includes(".docx")) {
        url = getWordUrl(doc);
      } else {
        url = await getPdfUrl(doc);
      }
      setRecentDocumentUrl(url);
    },
    [documentType]
  );

  const getDoc = useCallback(async () => {
    if (!prospectId || !proposalId || !documentType) {
      // Don't make request if information is missing
      // But don't set error, as we expect the information to be provided later
      return;
    }
    const CHECKOUT_ATTACHMENTS_URL = `${
      proposalToken ? "/customer" : ""
    }/prospect/${prospectId}/proposals/${proposalId}/attachments?documentType=${documentType}`;
    try {
      const docVersions: SplatCheckoutDocument[] = await AmplifyGet(
        hybridToken
          ? "Get Checkout Documents"
          : "Customer Get Checkout Documents",
        "OfferExpApi",
        CHECKOUT_ATTACHMENTS_URL,
        {
          headers: {
            Authorization: `Bearer ${hybridToken || proposalToken}`,
          },
        },
        logger
      );
      if (!docVersions || docVersions.length === 0) {
        setViewDocError(new Error(`No ${documentType} attachments found`));
        return;
      }
      await createUrl(findMostRecentAttachment(docVersions).url);
    } catch (e) {
      setViewDocError(
        new Error(`Failed to view ${documentType} checkout attachment`, {
          cause: e,
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    createUrl,
    hybridToken,
    proposalToken,
    documentType,
    prospectId,
    proposalId,
  ]);

  useEffect(() => {
    setIsLoading(true);
    setViewDocError(undefined);

    getDoc();
  }, [getDoc]);

  const viewDocument = useCallback(async () => {
    if (!recentDocumentUrl) {
      setViewDocError(
        new Error(`No ${documentType} document available to view`)
      );
      return;
    }

    window.open(recentDocumentUrl, "_blank");
  }, [documentType, recentDocumentUrl]);

  return {
    viewDocLoading: isLoading,
    viewDocError,
    viewDocument,
    resetError: () => {
      setViewDocError(undefined);
    },
  } as const;
};

export { useCheckoutDocViewing };
